// GI31 일반출고 — moms-be(2.0) 계약 타입.
// 요청/응답 키가 대문자 스네이크인 이유는 BE DTO 가 1.0 그리드 필드명을 그대로 유지하기 때문이다
// (OutboundGeneralController · GeneralOutboundOrderItem). 여기서 camelCase 로 바꾸면 양쪽이 어긋난다.

/** 조회 응답 행 — moms-be `GeneralOutboundOrderItem` 1:1. */
export interface GeneralOutboundItem {
  ID: number
  /** 화주코드 */
  SPR_NM: string
  /** 주문유형 */
  ORD_TYPE: string
  /** 배송유형 */
  TRS_TYPE: string
  UID: string
  BARCODE: string
  SKU_ID: string | null
  /** 상품코드 */
  GDS_CD: string
  /** 상품명 */
  GDS_NM: string
  /** 브랜드 */
  BRD_NM: string
  /** 옵션코드 */
  OPT_CD: number | null
  /** 옵션명 */
  GDS_OPT: string
  /** 스타일넘버 */
  STL_NO: string
  /** 사은품 */
  GIFT: string
  /** 주문번호 */
  ORD_NO: string
  /** 주문일련번호 */
  ORD_OPT_NO: string
  /** 주문수량 */
  ORD_QTY: number
  /** 주문자명 */
  ORD_NM: string
  /** 수령자 */
  TRS_NM: string
  /** 우편번호 */
  TRS_ZIP: string
  /** 주소 */
  TRS_ADDR: string
  /** 핸드폰 */
  TRS_MOBILE: string
  /** 전화번호 */
  TRS_PHONE: string
  /** 출고메시지 */
  TRS_MSG: string
  /** 출입방법 */
  TRS_PASS: string
  /** 특이사항 */
  PROC_NM: string
  /** 출고예정일자 */
  PLN_DATE: string
  ETC1: string
  ETC2: string
  INS_DATE: string
  INS_BY: string
  UPD_DATE: string
  UPD_BY: string
}

/** 화면 필터 — 화주코드만 필수다(BE `SearchGeneralOutboundFilter.shipperCode`). */
export interface GeneralOutboundFilter {
  SPR_NM: string
  ORD_NO?: string[]
  ORD_OPT_NO?: string[]
  ORD_TYPE?: string
  TRS_TYPE?: string
}

/** 조회 요청 — BE 는 이 객체를 문자열로 받아 다시 파싱한다(`params: string`). */
export interface SearchGeneralOutboundParams {
  MENU_CD: 'GI31_HEAD'
  USR_ID: string
  params: GeneralOutboundFilter
  page: number
  pageSize: number
  type?: string
}

/** 엑셀 다운로드 요청 — moms-be LOGISPRD-15805. `params` 는 조회와 같은 JSON 문자열이다. 응답은 비밀번호 zip. */
export interface GeneralOutboundDownloadParams {
  params: string
  reasonCode: string
  detailedReason: string
  password: string
}

export interface SearchGeneralOutboundRes {
  rtnChk: boolean
  rtnData: GeneralOutboundItem[]
  MENU_CD: string
  message?: string
  params: SearchGeneralOutboundParams & { totalCnt?: number }
}

/** 저장 편집 플래그 — moms-be `EditedFlag`. */
export const EDITED_FLAG = {
  insert: 'I',
  update: 'E',
  delete: 'D',
} as const

export type EditedFlag = (typeof EDITED_FLAG)[keyof typeof EDITED_FLAG]

/** 저장 대상 행 — moms-be `SaveGeneralOutboundOrderItem`. 신규 행은 ID 가 없다. */
export interface SaveGeneralOutboundItem {
  ID?: number | null
  SPR_NM: string
  UID?: string | null
  BARCODE: string
  GDS_CD?: string | null
  GDS_NM?: string | null
  BRD_NM?: string | null
  OPT_CD?: number | null
  GDS_OPT?: string | null
  STL_NO?: string | null
  GIFT?: string | null
  ORD_NO: string
  ORD_OPT_NO: string
  ORD_QTY: number
  ORD_NM: string
  TRS_ADDR: string
  TRS_ZIP: string
  TRS_MOBILE: string
  TRS_PHONE: string
  TRS_NM: string
  TRS_MSG?: string | null
  TRS_PASS?: string | null
  PROC_NM?: string | null
  TRS_TYPE?: string | null
  ORD_TYPE?: string | null
  PLN_DATE?: string | null
  EDITTED?: EditedFlag
  ETC1?: string | null
  ETC2?: string | null
  shipper_code?: string
  shipper_name?: string
}

/** 저장 요청 — BE 는 이 객체를 문자열로 받아 다시 파싱한다(`data: string`). */
export interface SaveGeneralOutboundPayload {
  MENU_CD: 'GI31'
  ITEM_CD: string
  USR_ID: string
  SPR_NM?: string
  DEPT_NM?: string
  shipper_code: string
  shipper_name?: string
  data: { headData: SaveGeneralOutboundItem[] }
}

export interface SaveGeneralOutboundRes {
  MENU_CD: string
  rtnChk: boolean
  message: string
  stus?: string
  /** 저장은 워크플로에 접수만 한다. 진행 상황은 이 키로 폴링한다. */
  progressKey?: string
  /** SKU 미해소로 막힌 바코드 전량(422). */
  failedBarcodes?: string[]
  errorCode?: string
}

/**
 * 확정 요청 — 저장과 달리 문자열 감싸기 없이 그대로 JSON 이다.
 *
 * BE `ConfirmGeneralOutboundRequestBody` 에는 `url` 필드도 있으나 읽기만 하고 쓰지 않는다.
 * 1.0 이 그리드 공통 전송부에서 붙이던 잔재라 여기서는 넣지 않는다.
 */
export interface ConfirmGeneralOutboundPayload {
  params: GeneralOutboundFilter & {
    USR_ID: string
    MENU_CD: 'GI31'
  }
  data: ConfirmGeneralOutboundItem[]
}

export interface ConfirmGeneralOutboundItem {
  ID: number
  SPR_NM: string
  ORD_NO: string
  ORD_OPT_NO: string
  ORD_TYPE?: string
  TRS_TYPE?: string
}

export interface ConfirmGeneralOutboundRes {
  result: { result: boolean; message: string }
  message: string
  MENU_CD: string
  failedBarcodes?: string[]
  errorCode?: string
}

export interface OutboundProgress {
  status: string
  processed: number
  total: number
  message: string
}

/** SKU 미해소 차단 식별값 — 저장·확정이 같은 값을 쓴다(moms-be `ERROR_OUTBOUND_SKU_UNMAPPED`). */
export const ERROR_SKU_UNMAPPED = 'ERROR_OUTBOUND_SKU_UNMAPPED'
